import styled from '@emotion/styled';
import {media, spacing, Visible} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';
import Link from 'next/link';

import {useNavItem} from './useNavItem';
import {transient} from 'Application/configs/emotionTransientProps';

import type {LinkField} from 'types/payload-types';

/**
 * The props for the `NavItem` component, which renders a single navigational link with optional testing identifier.
 * These props include the `link` object that defines the navigation target and behavior, as well as an optional `testId`
 * string used for end-to-end testing. The `testId` helps uniquely identify the component in automated test scenarios,
 * improving test reliability and targeting.
 */
interface ComponentProps {
    /**
     * The navigation link data that defines the label, type, href, and other properties required for rendering
     * and behavior. This field is required and is passed to the `useNavItem` hook for interpretation.
     */
    link: LinkField;
    /**
     * Optional callback function that is invoked when the navigation item is clicked by the user.
     * This function executes before any internal navigation logic and can be used to trigger additional
     * actions such as analytics tracking, menu closing, or custom business logic. The function receives
     * no parameters and returns void, making it suitable for side effects only.
     */
    onClick?(): void;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'NavItem'
     */
    testId?: string;
}

/**
 * A navigation item component that renders a clickable link with active state indication and smooth animations.
 * This component uses MobX observer pattern to reactively update when navigation state changes, and includes
 * an animated indicator that appears when the current route matches the link destination. The component leverages
 * the useNavItem hook to process link data and determine appropriate navigation properties.
 *
 * @param props        The component props.
 * @param props.link   The navigation link data that defines the label, type, href, and other properties required for rendering and behavior.
 * @param props.testId The unique identifier for testing purposes, used to target this component in automated tests.
 * @returns The rendered navigation item as a styled link element with optional active indicator.
 *
 * @example
 * ```tsx
 * <NavItem
 *     link={{
 *         type: 'reference',
 *         reference: { value: 'page-id', relationTo: 'pages' },
 *         label: 'Home'
 *     }}
 *     testId="main-nav-home"
 * />
 * ```
 */
const NavItem = observer(({link, onClick, testId = 'NavItem'}: ComponentProps) => {
    const {handleClick, href, isActive, isDownload, label, rel, target} = useNavItem(link, onClick);

    return (
        <StyledLink
            $isActive={isActive}
            data-cy={testId}
            download={isDownload}
            href={href}
            rel={rel}
            target={target}
            onClick={handleClick}
        >
            {label}
            {isActive && (
                <Visible isLoadingHtml lg xl xxl>
                    <ActiveIndicator layoutId="MainMenuActive" style={{originY: 'top'}} layout />
                </Visible>
            )}
        </StyledLink>
    );
});

NavItem.displayName = 'NavItem';

export {NavItem};

interface StyledLinkProps {
    $isActive: boolean;
}

const StyledLink = styled(Link, transient)<StyledLinkProps>`
    color: ${({theme}) => theme.colors.textPrimary};
    display: block;
    font-family: ${({theme}) => theme.fonts.Montserrat};
    font-size: 1.6rem;
    font-weight: 500;
    height: 100%;
    line-height: 1.6rem;
    outline: 0px solid transparent;
    padding-inline-end: ${spacing(4)};
    position: relative;
    text-align: right;
    text-decoration: underline hsla(from ${({theme}) => theme.colors.secondary} H S L / ${({$isActive}) => ($isActive ? 1 : 0)});
    text-decoration-thickness: 2px;
    text-underline-offset: 3px;
    transition: outline 0.2s ease-in-out, text-decoration 0.2s ease-in-out;
    white-space: nowrap;
    width: 100%;

    ${media('lg')} {
        display: initial;
        height: initial;
        padding-inline-end: initial;
        text-align: left;
        width: initial;
        text-decoration: underline hsla(from ${({theme}) => theme.colors.secondary} H S L / 0);
    }

    &:hover {
        text-decoration: underline hsla(from ${({theme}) => theme.colors.secondary} H S L / 1);
        text-decoration-thickness: 2px;
        text-underline-offset: 3px;
    }
    &:focus-visible {
        outline: 2px solid ${({theme}) => theme.colors.secondaryDark};
        outline-offset: 2px;
        text-decoration: underline hsla(from ${({theme}) => theme.colors.secondary} H S L / 1);
        text-decoration-thickness: 2px;
        text-underline-offset: 3px;
    }
`;

const ActiveIndicator = styled(motion.div, transient)`
    background-color: ${({theme}) => theme.colors.secondary};
    bottom: -1px;
    height: 2px;
    position: absolute;
    width: 100%;
`;